
import {
    CategoryModel,
    PlanItemBasicModel,
    PlanItemModel,
    PlanItemStateEnum,
    ProcessTypeEnum,
    QueryOperator,
    SortOrder,
    TenantUserModel,
} from '@/libs/Api';
import { CrudAction } from '@/libs/core/+state/models/crud-action';
import { CrudDataStats } from '@/libs/core/+state/models/crud-data-stats';
import { CrudGetter } from '@/libs/core/+state/models/crud-getter';
import { createCrudQueryPayload } from '@/libs/core/+state/models/crud-query-payload';
import { CrudQueryPredicate } from '@/libs/core/+state/models/crud-query-predicate';
import { planItemsStore } from '@/libs/plan-items/+state/store';
import Page from '@/Page.vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { categoriesStore } from '@/libs/categories/+state/store';
import { authStore } from '@/libs/auth/+store/store';
import { AuthGetter } from '@/libs/auth/models/auth-state';
import moment from 'moment';
import ApiService from '@/libs/core/api-service';
import ShiftTypeSelect from '@/libs/common/components-smart/ShiftTypeSelect.vue';

const b64toBlob = (b64Data: string, contentType = '', sliceSize = 512): Blob => {
    const byteCharacters = atob(b64Data);
    const byteArrays = [];

    for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
        const slice = byteCharacters.slice(offset, offset + sliceSize);

        const byteNumbers = new Array(slice.length);
        for (let i = 0; i < slice.length; i++) {
            byteNumbers[i] = slice.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        byteArrays.push(byteArray);
    }

    const blob = new Blob(byteArrays, { type: contentType });
    return blob;
};

@Component({
    components: { ShiftTypeSelect },
})
export default class ReportedChecklists extends Page {
    filterWorkplace: Array<string> | null = null;
    filterStation: Array<string> | null = null;
    filterCategory: Array<string> | null = null;
    filterAudit: Array<string> | null = null;
    filterFaultType: Array<string> | null = null;
    filterPlanItemState: Array<string> | null = null;
    filterShiftType: Array<string> | null = null;
    filterUser: Array<string> | null = null;
    filterSince: string | null = null;
    filterTill: string | null = null;
    filterActive: boolean | null = null;
    downloading = false;

    mobileFiltersShow = false;
    escalation = 0;
    pageIndex = 1;
    pageSize = 25;
    pagingChanged = false;
    showSecondLine = false;
    debounce: any = null;
    tenantUser: TenantUserModel = authStore.useGetter(AuthGetter.GetTenantUser) as TenantUserModel;
    protected ProcessTypeEnum: typeof ProcessTypeEnum = ProcessTypeEnum;

    get headers() {
        return [
            { text: this.$i18n.t('planItems.date'), value: 'time', align: 'left', sortable: false },
            { text: this.$i18n.t('planItems.template'), value: 'audit.name', align: 'left', sortable: false },
            { text: this.$i18n.t('planItems.category.name'), value: 'audit.category.name', align: 'left', sortable: false },
            { text: this.$i18n.t('planItems.worker'), value: 'tenantUser.user.name', align: 'left', sortable: false },
            { text: this.$i18n.t('planItems.workplace.name'), value: 'workplace.name', align: 'left', sortable: false },
            { text: this.$i18n.t('planItems.station.name'), value: 'station.name', align: 'left', sortable: false },
            { text: this.$i18n.t('planItems.shiftType.name'), value: 'shiftType.name', align: 'left', sortable: false },
            { text: this.$i18n.t('planItems.itemState'), value: 'planItemState', align: 'left', sortable: false },
            { text: '', value: 'id', align: 'left', sortable: false },
        ];
    }

    @Prop() tenantId!: string;

    @Watch('filterPlanItemState')
    filterStateChanged() {
        this.load();
    }

    @Watch('pageIndex')
    @Watch('pageSize')
    paginationChanged() {
        this.saveQuery();
        if (this.pagingChanged) {
            this.pagingChanged = false;
            return;
        }
        this.load();
    }

    @Watch('filterWorkplace')
    @Watch('filterStation')
    @Watch('filterAudit')
    @Watch('filterFaultType')
    @Watch('filterPlanItemState')
    @Watch('filterShiftType')
    @Watch('filterUser')
    @Watch('filterSince')
    @Watch('filterTill')
    @Watch('filterActive')
    @Watch('filterCategory')
    filtersChanged() {
        clearTimeout(this.debounce);
        this.debounce = setTimeout(() => {
            this.pageIndex = 1;
            this.escalation--;
            if (this.escalation <= 0) this.load();
        }, 500);
    }

    @Watch('itemsPerPage')
    itemsPerPageChanged(v: number) {
        window.localStorage.amsItemsPerPage = v.toString();
    }

    get loading(): boolean {
        return planItemsStore.useGetter(CrudGetter.PendingBasic);
    }

    get planItems(): PlanItemBasicModel[] {
        return [...planItemsStore.useGetter(CrudGetter.DataBasic)];
    }

    get totalRows(): number {
        return ({ ...planItemsStore.useGetter(CrudGetter.DataStatsBasic) } as CrudDataStats)?.rowCount;
    }

    get categories(): CategoryModel[] {
        return [...categoriesStore.useGetter(CrudGetter.Data)];
    }

    saveQuery() {
        this.setQuery('pageSize', this.pageSize?.toString());
        window.localStorage.ptPageSize = this.pageSize?.toString();
    }

    mounted() {
        if (this.getQueryInt('pageSize') != null) {
            this.pageSize = this.getQueryInt('pageSize') ?? 25;
        } else {
            if (window.localStorage.ptPageSize && Number(window.localStorage.ptPageSize)) {
                this.pageSize = Number(window.localStorage.ptPageSize);
            }
            this.saveQuery();
        }
        this.load();
        this.loadCategories();
    }

    load() {
        planItemsStore.dispatch(CrudAction.GetAllBasic, this.getPayload());
    }

    getFilters(): CrudQueryPredicate[] {
        let filters: CrudQueryPredicate[] = [{ field: 'processType', op: 'eq', comparand: ProcessTypeEnum.Checklist }];
        let workplaceFilters: CrudQueryPredicate[] = [];
        let stationFilters: CrudQueryPredicate[] = [];
        let categoryFilters: CrudQueryPredicate[] = [];
        let auditFilters: CrudQueryPredicate[] = [];
        let faultTypeFilters: CrudQueryPredicate[] = [];
        let userFilters: CrudQueryPredicate[] = [];
        let shiftTypeFilters: CrudQueryPredicate[] = [];
        let planItemStateFilters: CrudQueryPredicate[] = [];
        if (!this.tenantUser.accessAllCategories) {
            filters.push({ field: 'audit.category.categoryUsers.tenantUserId', op: 'eq', comparand: this.tenantUser.id! });
        }
        filters.push({ field: 'isAnswered', op: 'eq', comparand: true });
        if (this.filterWorkplace && this.filterWorkplace.length > 0) {
            this.filterWorkplace.forEach((e: string) => {
                if (e) {
                    workplaceFilters.push({
                        field: 'workplaceId',
                        op: 'eq',
                        comparand: e,
                        junction: 'or',
                    });
                }
            });
        }
        if (this.filterStation && this.filterStation.length > 0) {
            this.filterStation.forEach((e: string) => {
                if (e) {
                    stationFilters.push({
                        field: 'stationId',
                        op: 'eq',
                        comparand: e,
                        junction: 'or',
                    });
                }
            });
        }
        if (this.filterAudit && this.filterAudit.length > 0) {
            this.filterAudit.forEach((e: string) => {
                if (e) {
                    auditFilters.push({
                        field: 'auditId',
                        op: 'eq',
                        comparand: e,
                        junction: 'or',
                    });
                }
            });
        }
        if (this.filterCategory && this.filterCategory.length > 0) {
            this.filterCategory.forEach((e: string) => {
                if (e) {
                    categoryFilters.push({
                        field: 'audit.categoryId',
                        op: 'eq',
                        comparand: e,
                        junction: 'or',
                    });
                }
            });
        }
        if (this.filterFaultType && this.filterFaultType.length > 0) {
            this.filterFaultType.forEach((e: string) => {
                if (e) {
                    faultTypeFilters.push({
                        field: 'auditFaultTypeId',
                        op: 'eq',
                        comparand: e,
                        junction: 'or',
                    });
                }
            });
        }
        if (this.filterPlanItemState && this.filterPlanItemState.length > 0) {
            this.filterPlanItemState.forEach((e: string) => {
                if (e) {
                    planItemStateFilters.push({
                        field: 'planItemState',
                        op: 'eq',
                        comparand: e,
                        junction: 'or',
                    });
                }
            });
        }
        if (this.filterUser && this.filterUser.length > 0) {
            this.filterUser.forEach((e: string) => {
                if (e) {
                    userFilters.push({
                        field: 'planItemAuditTenantUsers.tenantUserId',
                        op: 'eq',
                        comparand: e,
                        junction: 'or',
                    });
                }
            });
        }
        if (this.filterShiftType && this.filterShiftType.length > 0) {
            this.filterShiftType.forEach((e: string) => {
                if (e) {
                    shiftTypeFilters.push({
                        field: 'shiftTypeId',
                        op: 'eq',
                        comparand: e,
                        junction: 'or',
                    });
                }
            });
        }
        if (workplaceFilters.length > 0) {
            filters.push({
                field: 'id',
                op: 'wrap',
                comparand: workplaceFilters,
            });
        }
        if (stationFilters.length > 0) {
            filters.push({
                field: 'id',
                op: 'wrap',
                comparand: stationFilters,
            });
        }
        if (auditFilters.length > 0) {
            filters.push({
                field: 'id',
                op: 'wrap',
                comparand: auditFilters,
            });
        }
        if (categoryFilters.length > 0) {
            filters.push({
                field: 'id',
                op: 'wrap',
                comparand: categoryFilters,
            });
        }
        if (faultTypeFilters.length > 0) {
            filters.push({
                field: 'id',
                op: 'wrap',
                comparand: faultTypeFilters,
            });
        }
        if (userFilters.length > 0) {
            filters.push({
                field: 'id',
                op: 'wrap',
                comparand: userFilters,
            });
        }
        if (workplaceFilters.length > 0) {
            filters.push({
                field: 'id',
                op: 'wrap',
                comparand: workplaceFilters,
            });
        }
        if (shiftTypeFilters.length > 0) {
            filters.push({
                field: 'id',
                op: 'wrap',
                comparand: shiftTypeFilters,
            });
        }
        if (planItemStateFilters.length > 0) {
            filters.push({
                field: 'planItemState',
                op: 'wrap',
                comparand: planItemStateFilters,
            });
        }
        if (this.filterSince && this.filterSince.length > 0) {
            filters.push({
                field: 'time',
                op: 'gte',
                comparand: moment(this.filterSince).format('YYYY-MM-DDT00:00:00'),
            });
        }
        if (this.filterTill && this.filterTill.length > 0) {
            filters.push({
                field: 'time',
                op: 'lte',
                comparand: moment(this.filterTill).format('YYYY-MM-DDT23:59:59'),
            });
        }
        if (this.filterActive == true) {
            filters.push({
                field: 'planItemState',
                op: 'in',
                comparand: [
                    PlanItemStateEnum.PerformedWithoutFindings,
                    PlanItemStateEnum.NeedsToCreateMeasures,
                    PlanItemStateEnum.NeedsToPerformMeasures,
                    PlanItemStateEnum.NeedsToVerifyMeasures,
                    PlanItemStateEnum.AllMeasuresVerified,
                    PlanItemStateEnum.Approved,
                    PlanItemStateEnum.Declined,
                    PlanItemStateEnum.WaitingForApproval,
                    PlanItemStateEnum.WaitingToPerform,
                ],
            });
        } else if (this.filterActive == false) {
            filters.push({
                field: 'planItemState',
                op: 'in',
                comparand: [
                    PlanItemStateEnum.ClosedWithoutFindings,
                    PlanItemStateEnum.ClosedWithIssueEscalation,
                    PlanItemStateEnum.Closed,
                ],
            });
        }
        return filters;
    }

    getPayload() {
        return createCrudQueryPayload<PlanItemModel>({ field: 'time', index: 1, order: 'desc' }, this.getFilters(), {
            pageIndex: this.pageIndex,
            pageSize: this.pageSize,
        });
    }

    exportClick() {
        const filters = this.getFilters();
        this.downloading = true;
        ApiService.api
            .exportPlanItemBatch(1, 999999, {
                predicates: filters.map((x) => {
                    return {
                        field: x.field,
                        op: x.op as QueryOperator,
                        comparand: x.comparand,
                    };
                }),
                orderby: [{ field: 'time', index: 1, order: SortOrder.Desc }],
            })
            .then((e) => {
                const res = e.data.returnValue!;
                const blob = b64toBlob(res.base64!, res.contentType!);
                const link = document.createElement('a');
                link.href = window.URL.createObjectURL(blob);
                link.setAttribute('download', res.filename!);
                document.body.appendChild(link);
                link.click();
                link.parentNode!.removeChild(link);
                this.downloading = false;
            })
            .catch(() => {
                this.downloading = false;
            });
    }

    loadCategories() {
        const filters: CrudQueryPredicate[] = [];
        if (!this.tenantUser.accessAllCategories) {
            filters.push({ field: 'categoryUsers.tenantUserId', op: 'eq', comparand: this.tenantUser.id! });
        }
        categoriesStore.dispatch(
            CrudAction.GetAll,
            createCrudQueryPayload<CategoryModel>({ field: 'name', index: 1, order: 'asc' }, filters)
        );
    }
}
